// AIAssistant.styles.ts

import { Sparkles as SparklesIcon } from 'lucide-react'
import { ButtonHTMLAttributes } from 'react'
import styled, { keyframes } from 'styled-components'
import Colors from '../../assets/styles/Colors'

// --- TypeScript Types ---

// This interface is needed for styling IconButton
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  'data-role'?: string
}

// --- Keyframes ---

const BounceAnimation = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`

// --- Styled Components ---

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: calc(100dvh - 150px);
  height: 100%;
  position: relative;
`

interface ChatAreaProps {
  isEmpty?: boolean
}

export const ChatArea = styled.div<ChatAreaProps>`
  flex: 1;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  justify-content: ${(props) => (props.isEmpty ? 'center' : 'flex-start')};
  padding: ${(props) => (props.isEmpty ? '0 1.5rem' : '2rem 1rem')};
  padding-bottom: ${(props) => (props.isEmpty ? '0' : '0')};
  /* Prevent layout shifts during streaming */
  min-height: 0;
  flex-shrink: 1;
  /* Smooth scrolling behavior */
  scroll-behavior: smooth;
  /* Ensure scrollbar is visible on the right side */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  /* Webkit scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }

  /* Prevent content from shifting during scroll */
  scroll-padding-bottom: 1rem;
`

// --- Message Styles ---

const MessageGroupBase = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const MessageGroup = styled(MessageGroupBase)`
  align-items: flex-start; /* For bot messages */
`

export const UserMessageWrapper = styled(MessageGroupBase)`
  align-items: flex-end; /* For user messages */
`

export const UserMessage = styled.div`
  /* background-color: #e8eaed; */
  color: #1f1f1f;
  padding: 0.75rem 1rem;
  /* border-radius: 1.25rem; */
  max-width: 70%;
  font-size: 0.9375rem;
  line-height: 1.5;
  white-space: normal;
`

export const AIMessageCard = styled.div`
  /* background-color: white; */
  /* border: 1px solid #e0e0e0; */
  /* border-radius: 1.25rem; */
  padding: 0;
  max-width: 100%;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); */
`
export const ChatAttachmentList = styled.div<{ align?: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  align-items: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
`

export const ChatAttachmentCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  background: ${Colors.white};
  border: 1px solid ${Colors.lightGrey};
  text-decoration: none;
  color: ${Colors.Black};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  max-width: 340px;
  cursor: pointer;

  &:hover {
    border-color: ${Colors.primaryRed};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`

export const ChatAttachmentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${Colors.lightRed};
  flex-shrink: 0;
`

export const ChatAttachmentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

export const ChatAttachmentName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${Colors.Black};
  word-break: break-word;
`

export const ChatAttachmentMeta = styled.span`
  font-size: 0.6875rem;
  color: ${Colors.secondaryGrey};
`

export const ChatAttachmentBadge = styled.span`
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  background: ${Colors.lightRed};
  color: ${Colors.primaryRed};
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`
export const AIMessageContent = styled.div`
  color: #1f1f1f;
  font-size: 0.9375rem;
  line-height: 1.6;
  white-space: normal;
  display: block;

  p,
  ul,
  ol {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }

  ul,
  ol {
    padding-left: 0rem;
  }

  li {
    margin-bottom: 0.25rem;
    line-height: 1.6;
    *::marker {
      color: ${Colors.primaryRed} !important;
    }
  }

  h2,
  h3 {
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #1f1f1f;
  }

  code {
    background-color: #f1f3f4;
    padding: 0.125rem 0.25rem;
    border-radius: 4px;
    font-size: 0.875rem;
  }

  pre {
    margin-top: 0.5rem;
    margin-bottom: 0.75rem;
  }

  /* animation */
  transition: all 0.15s ease-in-out;
  will-change: opacity, transform;

  &.new-chunk {
    opacity: 0;
    transform: translateY(4px);
    animation: fadeIn 0.3s forwards;
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

// --- Markdown Element Styles ---

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  color: #1f1f1f;
  font-size: 1rem;
`

// Using 'as any' to resolve TypeScript conflict with Lucide components
export const TitleIcon = styled(SparklesIcon as any)`
  color: #1a73e8;
  width: 1rem;
  height: 1rem;
`

export const List = styled.ul`
  list-style-type: disc;
  margin-left: 1.5rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  padding-left: 0;
  color: #3c4043;
  line-height: 1.6;

  ul {
    list-style-type: circle;
    margin-left: 1.25rem;
    margin-top: 0.25rem;
  }
`

export const OrderedList = styled.ol`
  list-style-type: decimal;
  margin-left: 1.5rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  padding-left: 0;
  color: #3c4043;
  line-height: 1.6;

  ul {
    list-style-type: disc;
    margin-left: 1.25rem;
    margin-top: 0.25rem;
  }
`

export const ListItem = styled.li`
  margin: 0.25rem 0;
  line-height: 1.6;
  color: #3c4043;
`

export const ParagraphStyled = styled.p`
  margin: 0.25rem 0;
  line-height: 1.6;
  color: #3c4043;
`

// --- Input Styles ---

// AIAssistant.styles.ts

interface InputContainerProps {
  hasMessages?: boolean
}

export const InputContainer = styled.div<InputContainerProps>`
  position: ${(props) => (props.hasMessages ? 'sticky' : 'absolute')};
  background-color: #fafbfb;
  left: 0;
  right: 0;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  z-index: 10;

  /* Always center horizontally, only animate vertically - but only when transitioning from empty to having messages */
  transform: ${(props) => (props.hasMessages ? 'translateY(0) !important' : 'translateY(-50%)')};

  /* Position vertically - fixed bottom when messages exist to prevent jumping */
  top: ${(props) => (props.hasMessages ? 'auto' : '50%')};
  bottom: ${(props) => (props.hasMessages ? '1.5rem' : 'auto')};

  /* Only transition when going from empty (no messages) to having messages - once transitioned, no more transitions */
  transition: ${(props) =>
    props.hasMessages
      ? 'none !important'
      : 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), top 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), bottom 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'};

  &::before {
    content: '';
    position: absolute;
    top: -1.5rem;
    left: 0;
    right: 0;
    height: 1.5rem;
    background: linear-gradient(to top, #fafbfb, rgba(250, 251, 251, 0));
    pointer-events: none;
    display: ${(props) => (props.hasMessages ? 'block' : 'none')};
    z-index: 0;
  }
`

export const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  background-color: ${Colors.white};
  /* Use transparent border and an outer ring via box-shadow to avoid clipping on the top side */
  border: 1px solid transparent;
  border-radius: 24px;
  padding: 0.5rem;
  gap: 0.75rem;
  /* Strong, even ring + soft shadow */
  box-shadow: 0 0 0 1px ${Colors.lightGrey}, 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s;
  position: relative;
  z-index: 1;

  &:focus-within {
    box-shadow: 0 0 0 2px ${Colors.primaryRed}, 0 6px 16px rgba(0, 0, 0, 0.08);
  }
`

export const IconButton = styled.button<IconButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${Colors.Black};
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;

  svg {
    color: ${Colors.white};
  }

  &:hover {
    color: ${Colors.white};
  }

  ${(props) =>
    props['data-role'] === 'add-file' &&
    `
    background-color: ${Colors.primaryRed};
    color: ${Colors.white};
    border-radius: 50%;
    width: 32px;
    height: 32px;
    padding: 0;
    
    svg {
      color: ${Colors.white};
    }
    
    &:hover {
        background-color: ${Colors.primaryRed};
        color: ${Colors.white};
        
        svg {
          color: ${Colors.white};
        }
    }
  `}
`
export const SendButton = styled(IconButton)`
  background-color: ${Colors.primaryRed};
  color: ${Colors.white};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  padding: 0;
  /* margin-right: 0.5rem; */

  svg {
    color: ${Colors.white};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0;
  }

  &:hover:not(:disabled) {
    background-color: ${Colors.primaryRed};
    color: ${Colors.white};
  }
`

export const Input = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9375rem;
  color: #1f1f1f;
  background: transparent;
  padding: 0.25rem 0.5rem;
  resize: none;
  min-height: 24px;
  max-height: 150px;
  overflow-y: auto;
  font-family: inherit;
  line-height: 1.5;

  &::placeholder {
    color: #9aa0a6;
  }

  /* Hide scrollbar for cleaner look */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
`

export const Disclaimer = styled.p`
  text-align: center;
  font-size: 0.8125rem;
  color: #9aa0a6;
  margin-top: 0.75rem;
  margin-bottom: 0;
`

// --- Typing Indicator Styles ---

export const Dot = styled.div`
  background-color: #5f6368;
  border-radius: 50%;
  width: 8px;
  height: 8px;
  margin: 0 2px;
  animation: ${BounceAnimation} 1.4s infinite ease-in-out both;

  &:nth-child(1) {
    animation-delay: -0.32s;
  }
  &:nth-child(2) {
    animation-delay: -0.16s;
  }
  &:nth-child(3) {
    animation-delay: 0s;
  }
`

export const TypingIndicatorWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  height: 1.5rem;
`
// Add these to your './AIAssistant.styles' file

export const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
`

export const AttachmentBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #3b3b4f; /* Example background */
  color: #fff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;

  // Style for the X icon button inside the badge
  ${IconButton} {
    margin-left: 4px;
    padding: 0;
    width: 16px;
    height: 16px;
    min-width: 16px;
    color: #ccc;
    &:hover {
      color: #fff;
    }
  }
`

// --- Menu Styles ---

export const MenuWrapper = styled.div`
  position: relative;
`

export const MenuDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 0;
  background-color: ${Colors.white};
  border-radius: 8px;
  padding: 0.5rem;
  min-width: 220px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid ${Colors.lightGrey};
  z-index: 1000;
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0.375rem;
  animation: ${(props) => (props.isOpen ? 'fadeInUp' : 'none')} 0.2s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.375rem 0.625rem;
  background: ${Colors.white};
  border: 1px solid ${Colors.lightGrey};
  color: ${Colors.Black};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;

  &:hover {
    border-color: ${Colors.primaryRed};
    background: ${Colors.lightRed};
  }
`

export const MenuDivider = styled.div`
  display: none;
`

export const MenuItemText = styled.span`
  flex: 1;
  text-align: left;
  font-weight: 400;
`

export const MenuItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border-radius: 4px;
  flex-shrink: 0;

  svg {
    color: ${Colors.primaryRed};
  }
`

export const MenuItemChevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${Colors.lightRed};
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${MenuItem}:hover & {
    background: ${Colors.primaryRed};
    color: ${Colors.white};
  }
`

// --- Slide Animation Styles ---

export const SlideContainer = styled.div<{ isSliding: boolean }>`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

export const SlideView = styled.div<{ isActive: boolean; direction: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  transform: ${(props) => {
    if (props.isActive) {
      return 'translateX(0)'
    }
    return props.direction === 'left' ? 'translateX(-100%)' : 'translateX(100%)'
  }};
  will-change: transform;
  z-index: ${(props) => (props.isActive ? 1 : 0)};
  pointer-events: ${(props) => (props.isActive ? 'auto' : 'none')};
  /* Add padding bottom to account for input container when active */
  padding-bottom: ${(props) => (props.isActive ? '10px' : '0')};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding-bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${Colors.Black};
  cursor: pointer;
  padding: 8px 12px;
  font-size: 14px;
  transition: opacity 0.2s;
  margin-bottom: 1rem;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`
